import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Save,
    X,
    FileText,
    DollarSign,
    MessageSquare,
    Loader2,
    CreditCard,
    Briefcase,
} from 'lucide-react';

import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import TextArea from '../components/ui/TextArea';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import InvoiceItemsTable from '../components/ui/InvoiceItemsTable';
import { useToast } from '../hooks/useToast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    fetchUtilCustomers,
    fetchUtilStaff,
    fetchCatentriesUtil,
    fetchAllBankAccounts,
    readTaxes,
    fetchDiscountRules,
    fetchStores, fetchStoresWhereStaffAssigned,
    fetchPOSCustomers,
} from '../apiHelper';
import { client } from '../apiHelper';

const NewReceipt = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [user, setUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        customerId: '',
        receiptDate: new Date().toISOString().slice(0, 16),
        storeId: '',
        salesPersonId: '',
        receiptNumber: '',
        note: '',
        paymentMethod: 'cash',
        referenceNumber: '',
        bankAccountId: '',
        sendReceipt: false,
    });

    const [lineItems, setLineItems] = useState([]);

    // Options
    const [customerOptions, setCustomerOptions] = useState([]);
    const [salesPersonOptions, setSalesPersonOptions] = useState([]);
    const [itemOptions, setItemOptions] = useState([]);
    const [taxOptions, setTaxOptions] = useState([]);
    const [discountOptions, setDiscountOptions] = useState([]);
    const [bankAccountOptions, setBankAccountOptions] = useState([]);

    // Loading
    const [loadingItems, setLoadingItems] = useState(true);
    const [loadingTaxes, setLoadingTaxes] = useState(true);
    const [loadingDiscounts, setLoadingDiscounts] = useState(true);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(true);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [loadingStaff, setLoadingStaff] = useState(false);

    const [storeOptions, setStoreOptions] = useState([]);
    const [loadingStores, setLoadingStores] = useState(true);
    // const [selectedStore, setSelectedStore] = useState('');

    // Load user
    useEffect(() => {
        const stored = localStorage.getItem('user');
        if (stored) setUser(JSON.parse(stored));
        else {
            showToast('Please log in', 'info');
            navigate('/login');
        }
    }, [showToast, navigate]);

    // Fetch customers
    const fetchCustomers = useCallback(async (searchQuery = '') => {
        if (!user || !formData.storeId) return;

        setLoadingCustomers(true);
        try {
            // const customers = await fetchUtilCustomers(user, searchQuery);
            const customers = await fetchPOSCustomers({ ...user, store_id: formData.storeId }, searchQuery);
            setCustomerOptions(
                customers.map(c => {
                    const u = c.user || {};
                    const name = [(u.first_name || '').trim(), (u.middle_name || '').trim(), (u.last_name || '').trim()]
                        .filter(Boolean)
                        .join(' ') || 'Unnamed Customer';
                    const logonId = u.credential?.logon_id || 'No ID';
                    return {
                        label: `${name} (${logonId})`,
                        value: c.member_id,
                    };
                })
            );
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to load customers', 'error');
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, formData.storeId, showToast]);

    useEffect(() => {
        if (user && formData.storeId) fetchCustomers();
    }, [user, formData.storeId, fetchCustomers]);

    // Fetch staff
    useEffect(() => {
        if (!user) return;
        const load = async () => {
            setLoadingStaff(true);
            try {
                const staff = await fetchUtilStaff(user);
                setSalesPersonOptions(
                    staff.map(s => ({
                        label: `${s.user.first_name} ${s.user.last_name}`,
                        value: s.member_id,
                    }))
                );
            } catch (err) {
                showToast(err.response?.data?.detail || 'Failed to load staff', 'error');
            } finally {
                setLoadingStaff(false);
            }
        };
        load();
    }, [user, showToast]);

    // Auto-select current user
    useEffect(() => {
        if (user?.users_id && salesPersonOptions.length > 0) {
            const me = salesPersonOptions.find(o => o.value === user.users_id);
            if (me) setFormData(p => ({ ...p, salesPersonId: me.value }));
        }
    }, [user?.users_id, salesPersonOptions]);

    // Fetch items, taxes, discounts, bank accounts (same as before)
    useEffect(() => {
        if (!user) return;
        const load = async () => {
            setLoadingItems(true);
            try {
                const items = await fetchCatentriesUtil(user);
                setItemOptions(
                    items.map(i => ({
                        value: i.catentry_id,
                        label: i.productname,
                        price: parseFloat(i.listprice),
                        total_quantity: i.total_quantity,
                        type: i.catalog_type,
                    }))
                );
            } catch (err) {
                showToast(err.response?.data?.detail || 'Failed to load items', 'error');
                setItemOptions([]);
            } finally {
                setLoadingItems(false);
            }
        };
        load();
    }, [user, showToast]);

    useEffect(() => {
        if (!user) return;
        const load = async () => {
            setLoadingTaxes(true);
            try {
                const taxes = await readTaxes(user);
                setTaxOptions(
                    taxes.map(t => ({
                        label: `${t.code} – ${t.identifier} (${t.rate.toFixed(1)}%)`,
                        value: t.taxcategory_id.toString(),
                        rate: parseFloat(t.rate),
                    }))
                );
            } catch {
                showToast('Failed to load taxes', 'error');
            } finally {
                setLoadingTaxes(false);
            }
        };
        load();
    }, [user, showToast]);

    useEffect(() => {
        if (!user) return;
        const load = async () => {
            setLoadingDiscounts(true);
            try {
                const rules = await fetchDiscountRules({ ...user, offset: 0, limit: 1000 });
                setDiscountOptions(
                    rules.map(r => ({
                        label: `${r.name} – ${r.description}`,
                        value: r.discountrule_id.toString(),
                        type: r.discount_type,
                        valueAmount: parseFloat(r.value),
                        appliesTo: r.applies_to,
                        targetProductId: r.target_product_id,
                        minimumPurchaseAmount: r.minimum_purchase_amount ? parseFloat(r.minimum_purchase_amount) : null,
                        minimumPurchaseQuantity: r.minimum_purchaseQuantity,
                        startDate: r.start_date,
                        endDate: r.end_date,
                        isActive: r.is_active,
                        customers: r.customers?.map(c => c.member_id) || [],
                        stores: r.stores?.map(s => s.store_id) || [],
                    }))
                );
            } catch {
                showToast('Failed to load discounts', 'error');
            } finally {
                setLoadingDiscounts(false);
            }
        };
        load();
    }, [user, showToast]);

    useEffect(() => {
        const load = async () => {
            setLoadingBankAccounts(true);
            try {
                const accounts = await fetchAllBankAccounts();
                setBankAccountOptions(
                    accounts
                        .filter(a => a.is_active && a.is_leaf)
                        .map(a => ({
                            label: `${a.name} (${a.account_code})`,
                            value: a.account_id,
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))
                );
            } catch {
                showToast('Failed to load bank accounts', 'error');
            } finally {
                setLoadingBankAccounts(false);
            }
        };
        load();
    }, [showToast]);

    useEffect(() => {
        if (!user) return;
        const loadStores = async () => {
            setLoadingStores(true);
            try {
                // const stores = await fetchStores(user);
                const stores = await fetchStoresWhereStaffAssigned(user);
                if (stores && stores.length > 0) {
                    const mappedStores = stores.map(store => ({
                        label: store.identifier || `Branch ${store.store_id}`,
                        value: store.store_id,
                        storeData: store
                    }))
                    // setSelectedStore(mappedStores[0].value);
                    setFormData(prev => ({ ...prev, 'storeId': mappedStores[0].value }));
                    setStoreOptions(mappedStores);
                } else {
                    setStoreOptions([]);
                }
            } catch (err) {
                showToast(err.response?.data?.detail || 'Failed to load branches', 'error');
                setStoreOptions([]);
            } finally {
                setLoadingStores(false);
            }
        };
        loadStores();
    }, [user, showToast]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(p => ({
            ...p,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleItemsChange = (items) => setLineItems(items);

    const validateForm = () => {
        if (!formData.customerId) return 'Customer is required.';
        if (!formData.salesPersonId) return 'Sales Person is required.';  // ← NOW REQUIRED
        if (!formData.receiptDate) return 'Receipt date is required.';
        if (lineItems.length === 0) return 'Add at least one item.';
        if (['card', 'bank_transfer'].includes(formData.paymentMethod) && !formData.bankAccountId)
            return 'Select bank account.';
        return null;
    };

    const handleSave = async () => {
        const error = validateForm();
        if (error) return showToast(error, 'error');

        setIsSubmitting(true);
        try {
            const itemsForBackend = lineItems.map(i => {
                const itemMeta = itemOptions.find(opt => opt.value === i.itemId);
                const rate = parseFloat(i.rate || itemMeta?.price || 0);
                const qty = parseFloat(i.quantity || 1);
                const subtotal = rate * qty;

                // Re-calculate discount and tax exactly like InvoiceItemRow does
                let discountAmount = 0;
                const selectedDiscount = discountOptions.find(d => d.value === i.discountId);

                if (selectedDiscount?.isActive) {
                    const now = new Date();
                    const validDate = (!selectedDiscount.startDate || now >= new Date(selectedDiscount.startDate)) &&
                        (!selectedDiscount.endDate || now <= new Date(selectedDiscount.endDate));

                    const validCustomer = selectedDiscount.customers.length === 0 ||
                        selectedDiscount.customers.includes(Number(formData.customerId));

                    if (validDate && validCustomer) {
                        const discountValue = parseFloat(selectedDiscount.valueAmount || selectedDiscount.value || 0);
                        const qty = parseFloat(i.quantity || 1);
                        const subtotal = rate * qty;

                        if (selectedDiscount.type === 'percentage' || selectedDiscount.discount_type === 'percentage') {
                            discountAmount = subtotal * (discountValue / 100);
                        }
                        else if (selectedDiscount.type === 'fixed' || selectedDiscount.discount_type === 'fixed') {
                            discountAmount = discountValue;
                        }
                        else if ((selectedDiscount.type === 'tiered_pricing' || selectedDiscount.discount_type === 'tiered_pricing')
                            && selectedDiscount.tiers?.length > 0) {

                            const applicableTier = selectedDiscount.tiers.find(tier =>
                                qty >= (tier.min_quantity || 0) &&
                                (!tier.max_quantity || qty <= tier.max_quantity)
                            );

                            if (applicableTier) {
                                discountAmount = parseFloat(applicableTier.tier_value || 0) * qty;
                            }
                        }
                    }
                }

                // Re-calculate discount and tax exactly like InvoiceItemRow does
                // let discountAmount = 0;
                // const selectedDiscount = discountOptions.find(d => d.value === i.discountId);
                // if (selectedDiscount?.isActive) {
                //     const now = new Date();
                //     const validDate = (!selectedDiscount.startDate || now >= new Date(selectedDiscount.startDate)) &&
                //         (!selectedDiscount.endDate || now <= new Date(selectedDiscount.endDate));
                //     const validCustomer = selectedDiscount.customers.length === 0 ||
                //         selectedDiscount.customers.includes(Number(formData.customerId));

                //     // Simplified — add full logic if needed, but this matches your working row
                //     if (validDate && validCustomer) {
                //         if (selectedDiscount.type === 'percentage') {
                //             discountAmount = subtotal * (selectedDiscount.valueAmount / 100);
                //         } else if (selectedDiscount.type === 'fixed') {
                //             discountAmount = selectedDiscount.valueAmount;
                //         }
                //     }
                // }

                const taxable = subtotal - discountAmount;
                const selectedTax = taxOptions.find(t => t.value === i.taxId);
                const taxRate = (selectedTax?.rate || 0) / 100;
                const taxAmount = taxable * taxRate;
                const total = taxable + taxAmount;

                return {
                    product_id: Number(i.itemId),
                    quantity: qty,
                    unit_price: rate,
                    subtotal: Number(subtotal.toFixed(2)),
                    discount_amount: Number(discountAmount.toFixed(2)),
                    tax_amount: Number(taxAmount.toFixed(2)),
                    total: Number(total.toFixed(2)),
                    discount_rule_id: i.discountId ? Number(i.discountId) : null,
                    tax_category_id: i.taxId ? Number(i.taxId) : null,
                };
            });

            const totals = itemsForBackend.reduce((acc, i) => ({
                subtotal: acc.subtotal + i.subtotal,
                discount: acc.discount + i.discount_amount,
                tax: acc.tax + i.tax_amount,
                total: acc.total + i.total,
            }), { subtotal: 0, discount: 0, tax: 0, total: 0 });

            const payload = {
                customer_id: Number(formData.customerId),
                store_id: Number(formData.storeId),
                receipt_date: formData.receiptDate,
                sales_person_id: Number(formData.salesPersonId),
                receipt_number: formData.receiptNumber.trim() || `REC-${Date.now()}`,
                note: formData.note.trim() || null,
                payment_method: formData.paymentMethod,
                reference_number: formData.referenceNumber.trim() || null,
                bank_account_id: formData.bankAccountId ? Number(formData.bankAccountId) : null,
                items: itemsForBackend,
                total_amount: Number(totals.total.toFixed(2)),
                subtotal: Number(totals.subtotal.toFixed(2)),
                discount_total: Number(totals.discount.toFixed(2)),
                tax_total: Number(totals.tax.toFixed(2)),
                status: 'PAID',
                send_receipt: formData.sendReceipt,
            };

            console.log('FINAL PAYLOAD →', payload);

            // Uncomment when ready
            const response = await client.postData('/sales/create-receipt', payload);
            showToast(response?.msg || 'Receipt created successfully!', 'success');
            // navigate('/sales/receipts');

        } catch (err) {
            showToast(err.response?.data?.msg || 'Failed to create receipt', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!user) return null;

    const showBankField = ['card', 'bank_transfer'].includes(formData.paymentMethod);

    return (
        <div className="w-full mx-auto p-4 md:p-8 max-w-7xl bg-zinc-50/30 min-h-screen">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Modern Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2">
                        <div>
                            <div className="flex items-center gap-2.5 mb-1">
                                <div className="p-2 bg-indigo-50 rounded-xl border border-indigo-100 text-indigo-600 shadow-sm">
                                    <FileText size={22} />
                                </div>
                                <h1 className="text-2xl font-bold tracking-tight text-zinc-900">
                                    New Sales Receipt
                                </h1>
                            </div>
                            <p className="text-zinc-500 text-sm pl-1">Record immediate payment from customer.</p>
                        </div>
                    </div>

                    {/* Branch & Sales Person */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <SearchableSelectBox
                                name="storeId"
                                label="Store / Branch"
                                required
                                value={formData.storeId}
                                onChange={handleInputChange}
                                options={storeOptions}
                                loading={loadingStores}
                                placeholder="Select store..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                            <SearchableSelectBox
                                name="salesPersonId"
                                label="Sales Person"
                                required
                                isDisabled
                                value={formData.salesPersonId}
                                onChange={handleInputChange}
                                options={salesPersonOptions}
                                loading={loadingStaff}
                                placeholder="Loading your name..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                disabled={true}
                            />
                        </div>
                    </Card>

                    {/* Customer & Core Metadata */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white space-y-6">
                        <div className="p-5 bg-zinc-50/70 border border-zinc-200/50 rounded-xl">
                            <SearchableSelectBox
                                name="customerId"
                                label="Customer"
                                required
                                value={formData.customerId}
                                onChange={handleInputChange}
                                options={customerOptions}
                                onSearch={fetchCustomers}
                                loading={loadingCustomers}
                                placeholder="Search customer..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2">
                            <Input
                                name="receiptNumber"
                                label="Receipt Number"
                                placeholder="Auto-generated if left blank"
                                value={formData.receiptNumber}
                                onChange={handleInputChange}
                            />
                            <Input
                                type="datetime-local"
                                name="receiptDate"
                                label="Receipt Date"
                                required
                                value={formData.receiptDate}
                                onChange={handleInputChange}
                                step="60"
                            />
                        </div>
                    </Card>

                    {/* Payment Method */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white space-y-5">
                        <div>
                            <label className="block text-xs font-semibold text-zinc-700 mb-3">Payment Method</label>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                {[
                                    { id: 'cash', label: 'Cash' },
                                    { id: 'bank_transfer', label: 'Bank Transfer' },
                                    { id: 'pos', label: 'POS' },
                                    { id: 'cheque', label: 'Cheque' },
                                ].map((channel) => (
                                    <button
                                        key={channel.id}
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, paymentMethod: channel.id }))}
                                        className={`p-3.5 border text-left rounded-xl transition-all duration-150 flex flex-col justify-between h-20 ${formData.paymentMethod === channel.id
                                            ? 'border-indigo-600 bg-indigo-50/40 shadow-xs ring-1 ring-indigo-500/20'
                                            : 'border-zinc-200 bg-white hover:bg-zinc-50/60'}`}
                                    >
                                        <div className={`w-2.5 h-2.5 rounded-full self-end ${formData.paymentMethod === channel.id ? 'bg-indigo-600 shadow-xs' : 'bg-zinc-200'}`} />
                                        <span className={`text-xs font-bold tracking-tight ${formData.paymentMethod === channel.id ? 'text-indigo-900' : 'text-zinc-700'}`}>
                                            {channel.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <AnimatePresence mode="wait">
                            {showBankField && (
                                <motion.div
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -6 }}
                                    transition={{ duration: 0.15 }}
                                    className="grid grid-cols-1 md:grid-cols-2 gap-5 p-4 bg-zinc-50 border border-zinc-200/60 rounded-xl mt-2"
                                >
                                    <SearchableSelectBox
                                        name="bankAccountId"
                                        label="Paid Into Account"
                                        required
                                        value={formData.bankAccountId}
                                        onChange={handleInputChange}
                                        options={bankAccountOptions}
                                        loading={loadingBankAccounts}
                                        placeholder="Select account..."
                                        menuPortalTarget={document.body}
                                    />
                                    <Input
                                        name="referenceNumber"
                                        label="Reference / Transaction ID (Optional)"
                                        placeholder="e.g. TRX123456"
                                        value={formData.referenceNumber}
                                        onChange={handleInputChange}
                                    />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Card>

                    {/* Items Section */}
                    <div className="space-y-3 pt-2">
                        <div className="flex items-center justify-between px-1">
                            <h3 className="font-bold text-sm text-zinc-800 flex items-center gap-2">
                                <Briefcase size={16} className="text-indigo-600" /> Receipt Items
                            </h3>
                            <span className="text-[11px] text-zinc-400 bg-zinc-100 border border-zinc-200/60 px-2 py-0.5 rounded-full font-medium">
                                Currency: ₦
                            </span>
                        </div>

                        {loadingItems || loadingTaxes || loadingDiscounts ? (
                            <Card className="p-12 border-zinc-200/60 shadow-xs rounded-xl bg-white text-center">
                                <Loader2 className="animate-spin h-8 w-8 mx-auto text-indigo-600 mb-3" />
                                <p className="text-zinc-500 text-xs font-medium">Loading catalog components...</p>
                            </Card>
                        ) : (
                            <div className="border border-zinc-200/60 shadow-xs rounded-xl bg-white overflow-hidden transition-all">
                                <InvoiceItemsTable
                                    itemOptions={itemOptions}
                                    taxOptions={taxOptions}
                                    discountOptions={discountOptions}
                                    currencySymbol="₦"
                                    onItemsChange={handleItemsChange}
                                    customerId={Number(formData.customerId) || null}
                                    storeId={formData.storeId}
                                    allItems={lineItems}
                                />
                            </div>
                        )}
                    </div>

                    {/* Notes */}
                    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white">
                        <div className="flex items-center gap-2 mb-4">
                            <MessageSquare size={16} className="text-zinc-400" />
                            <h3 className="font-bold text-xs text-zinc-500 uppercase tracking-wider">Customer Note (Optional)</h3>
                        </div>
                        <TextArea
                            name="note"
                            placeholder="Thank you for your business!"
                            value={formData.note}
                            onChange={handleInputChange}
                            rows={4}
                        />
                    </Card>

                    {/* Send Receipt Checkbox */}
                    <Card className="p-4 flex items-center gap-3 border-zinc-200/60">
                        <input
                            type="checkbox"
                            id="sendReceipt"
                            name="sendReceipt"
                            checked={formData.sendReceipt}
                            onChange={handleInputChange}
                            className="h-5 w-5 rounded text-indigo-600 focus:ring-indigo-500 border-zinc-300"
                        />
                        <label htmlFor="sendReceipt" className="text-sm font-medium select-none cursor-pointer">
                            Send receipt to the customer via email/SMS
                        </label>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex flex-col-reverse sm:flex-row justify-end items-center gap-3.5 pt-6 border-t border-zinc-200/80">
                        <Button
                            variant="ghost"
                            className="w-full sm:w-auto flex items-center justify-center text-zinc-600 hover:text-zinc-900 font-medium"
                            onClick={() => navigate(-1)}
                            disabled={isSubmitting}
                        >
                            <X size={16} className="mr-2" /> Cancel
                        </Button>
                        <Button
                            className="w-full sm:w-auto flex items-center justify-center font-semibold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-indigo-100 transition-all duration-150 px-6"
                            variant="primary"
                            onClick={handleSave}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 size={16} className="mr-2 animate-spin" /> Creating...
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="mr-2" /> Create Receipt
                                </>
                            )}
                        </Button>
                    </div>

                </div>

                {/* Right Side Summary */}
                <div className="lg:col-span-1 lg:sticky lg:top-6">
                    <Card className="p-6 border-zinc-200/60 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl bg-white relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-[3px] bg-indigo-600" />
                        <div className="flex items-center justify-between mb-5 pb-3 border-b border-zinc-100">
                            <div className="flex items-center gap-2">
                                <CreditCard size={18} className="text-indigo-600" />
                                <h3 className="font-bold text-xs text-zinc-700 uppercase tracking-wide">Summary</h3>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="p-3.5 bg-zinc-50 border border-zinc-100 rounded-lg">
                                <p className="text-[11px] font-bold uppercase tracking-tight text-zinc-400 mb-0.5">Amount Paid</p>
                                <p className="text-2xl font-extrabold text-zinc-900 tracking-tight">
                                    ₦{lineItems.reduce((sum, item) => {
                                        const selectedItem = itemOptions.find(opt => opt.value === item.itemId);
                                        const price = parseFloat(item.rate || selectedItem?.price || 0);
                                        const qty = parseFloat(item.quantity || 1);
                                        return sum + (price * qty);
                                    }, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>

            </div>
        </div>
    );
};

export default NewReceipt;

